import { css } from '@emotion/react';
import {
  motion, useAnimate,
} from 'motion/react';
import React, {
  type FC, useCallback,
  useEffect, useImperativeHandle,
} from 'react';
import type { Theme } from '~/_shared/themes/theme.model';

const wrapperStyle = ({ isOpen }: { isOpen: boolean }) => (theme: Theme) => css({
  display: 'flex',
  borderLeft: isOpen ? `1px solid ${theme.lineColors.basePrimary}` : '',
  position: 'relative',
});

const sidebarContentStyle = ({ width, isMobileScreen }: { width: number; isMobileScreen: boolean }) => css({
  zIndex: 0,
  height: '100%',
  width: isMobileScreen ? '100%' : width,
});

const slidingOverlayStyle = (theme: Theme) => css({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  zIndex: 10,
  backgroundColor: theme.backgroundColors.primary,
});

export type RightSidebarAnimationControllerMethods = Readonly<{
  playSlideOutAnimation: () => void;
}>;

type RightSidebarProps = Readonly<{
  animationControllerRef?: React.Ref<RightSidebarAnimationControllerMethods>;
  children?: React.ReactElement;
  isMobileScreen: boolean;
  slidingAnimationDuration: number;
  width: number;
}>;

export const RightSidebarComponent: FC<RightSidebarProps> = (props) => {
  const [overlayScope, overlayAnimate] = useAnimate();
  const [paneScope, paneAnimate] = useAnimate();

  const getPaneInitial = useCallback(() => ({
    width: 0,
    overflow: props.isMobileScreen ? 'unset' : 'clip',
  }), [props.isMobileScreen]);

  const getPaneAnimate = useCallback(() => ({
    width: props.isMobileScreen ? '100%' : props.width,
    overflow: props.isMobileScreen ? 'unset' : 'clip',
    transition: { duration: props.slidingAnimationDuration / 1000 },
  }), [props.width, props.isMobileScreen, props.slidingAnimationDuration]);

  const getOverlaySlideOutSequence = useCallback(async () => {
    await overlayAnimate(overlayScope.current, { width: props.width, duration: 0 });
    await overlayAnimate(overlayScope.current, {
      width: 0,
      transition: { duration: props.slidingAnimationDuration / 1000 },
    });
  }, [overlayAnimate, overlayScope, props.slidingAnimationDuration, props.width]);

  useImperativeHandle(props.animationControllerRef, () => ({
    playSlideOutAnimation: () => {
      getOverlaySlideOutSequence();
    },
  }), [getOverlaySlideOutSequence]);

  useEffect(() => {
    paneAnimate(paneScope.current, getPaneAnimate());
  }, [getPaneAnimate, paneAnimate, paneScope]);

  return (
    <div css={wrapperStyle({ isOpen: props.width > 0 })}>
      <motion.div
        ref={paneScope}
        initial={getPaneInitial()}
      >
        <div css={sidebarContentStyle({ width: props.width, isMobileScreen: props.isMobileScreen })}>
          {props.children}
        </div>
        <motion.div
          ref={overlayScope}
          css={slidingOverlayStyle}
          initial={{ width: 0 }}
        />
      </motion.div>
    </div>
  );
};
